// auth.js
// Authentication store module
import authService from '@/services/authService';
import currentUserPermissionService from '@/services/currentUserPermissionService';

// Initial state
const state = {
  user: JSON.parse(localStorage.getItem('user')) || null,
  token: localStorage.getItem('token') || null,
  permissions: JSON.parse(localStorage.getItem('userPermissions')) || [],
  loading: false,
  error: null,
  unverifiedEmail: null
};



// Getters
const getters = {
  isAuthenticated: (state) => !!state.token,
  currentUser: (state) => state.user,
  isSuperAdmin: (state) => !!state.user?.isSuperAdmin,
  authError: (state) => state.error,
  isLoading: (state) => state.loading,
  userPermissions: (state) => state.permissions,
  hasPermission: (state) => (permissionName) => {
    return state.permissions.some(p => {
      const name = p.name ?? p.Name ?? '';
      const granted = p.isGranted ?? p.IsGranted ?? true;
      return name === permissionName && granted;
    });
  },
  hasAnyPermission: (state) => (permissionNames) => {
    return permissionNames.some(permName =>
      state.permissions.some(p => {
        const name = p.name ?? p.Name ?? '';
        const granted = p.isGranted ?? p.IsGranted ?? true;
        return name === permName && granted;
      })
    );
  }
};

// Actions
const actions = {
  // Login action
  async login({ commit, dispatch, state }, credentials) {
    commit('LOGIN_REQUEST');

    try {
      // Use the actual authService
      const result = await authService.login(credentials);

      // On successful login, set the user and token in state
      commit('LOGIN_SUCCESS', {
        user: result.user,
        token: result.token
      });

      // Load user permissions after successful login — also updates isSuperAdmin on state.user
      await dispatch('loadUserPermissions');

      // Request Firebase notification permission after successful login
      try {
        const { requestNotificationPermission } = await import('@/plugins/firebase');
        await requestNotificationPermission();
      } catch (firebaseError) {
        console.warn('Firebase notification setup failed:', firebaseError);
      }

      return { success: true, user: state.user };
    } catch (error) {
      commit('LOGIN_FAILURE', error.message || 'Login failed');
      // Re-throw the error so the component can handle specific error types
      throw error;
    }
  },

  // Set auth data from existing result (no API call)
  async setAuthData({ commit, dispatch, state }, result) {
    commit('LOGIN_REQUEST');

    try {
      // Set the user and token in state from existing result
      commit('LOGIN_SUCCESS', {
        user: result.user,
        token: result.token
      });

      // Load user permissions after successful login — also updates isSuperAdmin on state.user
      await dispatch('loadUserPermissions');

      // Request Firebase notification permission after successful login
      try {
        const { requestNotificationPermission } = await import('@/plugins/firebase');
        await requestNotificationPermission();
      } catch (firebaseError) {
        console.warn('Firebase notification setup failed:', firebaseError);
      }

      return { success: true, user: state.user };
    } catch (error) {
      commit('LOGIN_FAILURE', error.message || 'Setting auth data failed');
      throw error;
    }
  },
  
  // Login with 2FA action for super admin
  async loginWith2FA({ commit, dispatch, state }, credentials) {
    commit('LOGIN_REQUEST');

    try {
      // Use the 2FA login method
      const result = await authService.loginWith2FA(credentials);

      // On successful login, set the user and token in state
      commit('LOGIN_SUCCESS', {
        user: result.user,
        token: result.token
      });

      // Load user permissions after successful login — also updates isSuperAdmin on state.user
      await dispatch('loadUserPermissions');

      // Request Firebase notification permission after successful login
      try {
        const { requestNotificationPermission } = await import('@/plugins/firebase');
        await requestNotificationPermission();
      } catch (firebaseError) {
        console.warn('Firebase notification setup failed:', firebaseError);
      }

      return { success: true, user: state.user };
    } catch (error) {
      commit('LOGIN_FAILURE', error.message || '2FA Login failed');
      // Re-throw the error so the component can handle specific error types
      throw error;
    }
  },
  
  // Register action
  async register({ commit }) {
    commit('REGISTER_REQUEST');
    
    try {
      // Simulate API call for now
      // Later replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      commit('REGISTER_SUCCESS');
      
      return { success: true };
    } catch (error) {
      commit('REGISTER_FAILURE', error.message || 'Registration failed');
      return { success: false, error: error.message || 'Registration failed' };
    }
  },
  
  // Check authentication status
  async checkAuth({ commit, state, dispatch }) {
    if (!state.token) return;
    
    try {
      await authService.checkAuth();
      // If auth check succeeds, load user permissions
      await dispatch('loadUserPermissions');
    } catch (error) {
      // If auth check fails, log out
      commit('SET_USER', null);
      commit('SET_TOKEN', null);
      commit('SET_PERMISSIONS', []);
    }
  },
  
  // Load user permissions
  async loadUserPermissions({ commit, state }) {
    try {
      const permissions = await currentUserPermissionService.getCurrentUserPermissions();
      commit('SET_PERMISSIONS', permissions);

      // Derive isSuperAdmin from permissions — handles both camelCase and PascalCase field names.
      // When isGranted is absent, the server only returns granted permissions, so treat it as true.
      const isSuperAdmin = permissions.some(p => {
        const name = p.name ?? p.Name ?? '';
        const granted = p.isGranted ?? p.IsGranted ?? true;
        return name.startsWith('Supplier.SuperAdmin.') && granted;
      });
      if (state.user) {
        const updatedUser = { ...state.user, isSuperAdmin };
        commit('SET_USER', updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      commit('SET_PERMISSIONS', []);
    }
  },
  
  // Resend email verification
  async resendVerificationEmail({ commit, state }) {
    if (!state.unverifiedEmail) {
      throw new Error('No email address available for verification');
    }
    
    commit('SET_LOADING', true);
    
    try {
      await authService.resendEmailVerification(state.unverifiedEmail);
      commit('SET_LOADING', false);
      return { success: true };
    } catch (error) {
      commit('SET_LOADING', false);
      throw error;
    }
  },
  
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  // Logout action
  async logout({ commit }) {
    try {
      // Remove Firebase notification token before logout
      const { removeNotificationToken } = await import('@/plugins/firebase');
      await removeNotificationToken();
    } catch (firebaseError) {
      console.warn('Firebase token removal failed:', firebaseError);
    }
    
    // Clear auth state
    commit('LOGOUT');
  }
};

// Mutations
const mutations = {
  SET_USER(state, user) {
    state.user = user;
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_LOADING(state, status) {
    state.loading = status;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  setUnverifiedEmail(state, email) {
    state.unverifiedEmail = email;
  },
  LOGIN_REQUEST(state) {
    state.loading = true;
    state.error = null;
  },
  LOGIN_SUCCESS(state, { user, token }) {
    state.loading = false;
    state.user = user;
    state.token = token;
    state.error = null;
    
    // Persist to localStorage
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
  },
  LOGIN_FAILURE(state, error) {
    state.loading = false;
    state.user = null;
    state.token = null;
    state.permissions = [];
    state.error = error;
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('userPermissions');
  },
  REGISTER_REQUEST(state) {
    state.loading = true;
    state.error = null;
  },
  REGISTER_SUCCESS(state) {
    state.loading = false;
    state.error = null;
  },
  REGISTER_FAILURE(state, error) {
    state.loading = false;
    state.error = error;
  },
  LOGOUT(state) {
    state.user = null;
    state.token = null;
    state.permissions = [];
    state.error = null;
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('userPermissions');
  },
  SET_PERMISSIONS(state, permissions) {
    state.permissions = permissions;
    
    // Persist to localStorage
    localStorage.setItem('userPermissions', JSON.stringify(permissions));
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}; 